import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { useAppDispatch } from "integrations/redux/hooks"
import { setGlobalNotificationContext } from "user/profile/slice"
import {
    useUpdateMaintenanceRecord,
    useDeleteMaintenanceRecord,
} from "features/pilot-tracking/maintenance/MaintenanceRecordQueries"
import {
    useConfirmationModalContext,
    getGenericConfirmationModalComponent,
} from "features/modals/WorkspaceConfirmationModalV2"
import { MaintenanceRecordForm, MaintenanceRecordFormValues } from "./MaintenanceRecordForm"

interface EditMaintenanceRecordModalProps {
    record: {
        id: string
        date_performed: Date
        performed_by: string | null
        notes: string | null
        skipped: boolean
        "template.instructions": string | null
    }
    onClose: () => void
    onSuccess?: () => void
}

const DeleteRecordConfirmModal = getGenericConfirmationModalComponent({
    title: "Delete Record?",
    message: "Are you sure you want to delete this record?",
    confirmText: "Discard Changes",
})

export function EditMaintenanceRecordModal({
    record,
    onClose,
    onSuccess,
}: EditMaintenanceRecordModalProps) {
    const dispatch = useAppDispatch()
    const modal = useConfirmationModalContext()
    const { updateMaintenanceRecord, isMutating } = useUpdateMaintenanceRecord()
    const { deleteMaintenanceRecord, isMutating: isDeleting } = useDeleteMaintenanceRecord()

    const handleSubmit = async ({
        date_performed,
        performed_by,
        notes,
        skipped,
    }: MaintenanceRecordFormValues) => {
        try {
            await updateMaintenanceRecord({
                recordId: record.id,
                body: {
                    date_performed,
                    performed_by: performed_by || null,
                    notes: notes || null,
                    skipped,
                },
            })
            dispatch(
                setGlobalNotificationContext({
                    message: "Maintenance record has been saved.",
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 5000,
                })
            )
            onSuccess?.()
            onClose()
        } catch {
            dispatch(
                setGlobalNotificationContext({
                    message: "Failed to save the maintenance record. Please try again.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        }
    }

    const handleDelete = async () => {
        await modal.confirm({
            RenderComponent: DeleteRecordConfirmModal,
            asyncConfirmCallback: async () => {
                try {
                    await deleteMaintenanceRecord(record.id)
                    dispatch(
                        setGlobalNotificationContext({
                            message: "Maintenance record has been deleted.",
                            status: GlobalNotificationStatus.success,
                            timeoutInMs: 5000,
                        })
                    )
                    onSuccess?.()
                    onClose()
                } catch {
                    dispatch(
                        setGlobalNotificationContext({
                            message: "Failed to delete the maintenance record. Please try again.",
                            status: GlobalNotificationStatus.error,
                            timeoutInMs: 5000,
                        })
                    )
                }
            },
        })
    }

    return (
        <HighPriorityNovaModalContainer
            className="max-w-xl w-full"
            title="Edit Maintenance Record"
            closeModal={onClose}
        >
            <MaintenanceRecordForm
                templateInstructions={record["template.instructions"]}
                initialValues={{
                    date_performed:
                        record.date_performed instanceof Date
                            ? record.date_performed.toISOString().slice(0, 10)
                            : record.date_performed,
                    performed_by: record.performed_by ?? "",
                    notes: record.notes ?? "",
                    skipped: record.skipped,
                }}
                onSubmit={handleSubmit}
                onCancel={onClose}
                onDelete={handleDelete}
                isSubmitting={isMutating || isDeleting}
                submitLabel="Save Record"
            />
        </HighPriorityNovaModalContainer>
    )
}
